<template>
  <div class="min-h-screen bg-slate-50 dark:bg-slate-900">
    <Navbar />

    <section class="container py-20 md:py-28">
      <div class="w-full max-w-4xl mx-auto space-y-4">
        <div
          class="p-5 bg-white border rounded-xl border-slate-200 dark:bg-slate-800 dark:border-slate-700"
        >
          <div class="flex flex-wrap items-center justify-between gap-3 pb-4 mb-4 border-b border-slate-200/70 dark:border-slate-700/70">
            <div>
              <h4
                class="text-base font-semibold text-slate-900 dark:text-white"
              >
                {{ t("user_type") }}
              </h4>
              <p class="text-xs text-slate-500 dark:text-slate-400">
                {{ t("selected") }}:
                <span class="font-semibold text-primary">
                  {{ currentClientTypeLabel }}
                </span>
              </p>
            </div>

            <div class="flex flex-wrap gap-2">
              <RouterLink
                v-for="tItem in clientTypes"
                :key="tItem.key"
                :to="toPath(tItem.key)"
                class="px-4 py-2 text-xs font-semibold transition border rounded-lg"
                :class="
                  isActiveType(tItem.key)
                    ? 'border-primary/60 text-primary bg-secondary/20'
                    : 'border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'
                "
              >
                {{ tItem.label }}
              </RouterLink>
            </div>
          </div>

          <div class="mb-3">
            <h3 class="text-sm font-semibold text-slate-900 dark:text-white">
              {{ t("client_info") || "Client Information" }}
            </h3>
            <p class="text-xs text-slate-500 dark:text-slate-400">
              {{
                t("client_info_hint") ||
                "Organization details used to set up your profile."
              }}
            </p>
          </div>
          <!-- STEP 0: CLIENT INFO -->
          <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
            <BaseInput
              :label="t('org_name')"
              v-model="form.client.name"
              :error="errors.client_name"
              :placeholder="t('org_name_ph')"
            />

            <BaseInput
              :label="t('activity')"
              v-model="form.client.activity"
              :error="errors.activity"
              :placeholder="t('activity_ph')"
            />

            <BaseInput
              :label="t('manager_name')"
              v-model="form.client.manager_name"
              :error="errors.manager_name"
              :placeholder="t('manager_name_ph')"
            />

            <BaseSelect
              :label="t('client_type')"
              v-model="form.client.client_type"
              :options="clientTypeOptions"
              :error="errors.client_type"
              :hint="t('choose_client_type_hint')"
            />

            <BaseTextarea
              class="md:col-span-2"
              :label="t('simple_description')"
              v-model="form.client.activity_description"
              :hint="t('simple_description_hint')"
              :placeholder="t('simple_description_ph')"
              :error="errors.activity_description"
            />
          </div>

          <div class="my-6 h-px bg-slate-200 dark:bg-slate-700"></div>

          <div class="mb-4">
            <h3 class="text-sm font-semibold text-slate-900 dark:text-white">
              {{ t("account_info") || "Account Information" }}
            </h3>
            <p class="text-xs text-slate-500 dark:text-slate-400">
              {{
                t("account_info_hint") ||
                "Login details and contact information for this account."
              }}
            </p>
          </div>

          <!-- STEP 1: USER INFO + DECLARATIONS -->
          <div class="grid grid-cols-1 gap-4 mt-6 md:grid-cols-2">
            <BaseInput
              :label="t('official_email_label')"
              v-model="form.user.email"
              :error="errors.email"
              type="email"
              :placeholder="t('official_email_ph')"
            />

            <BaseInput
              :label="t('mobile')"
              v-model="form.user.phone"
              :error="errors.phone"
              inputmode="tel"
              :placeholder="t('mobile_ph')"
            />

            <BaseInput
              :label="t('password')"
              v-model="form.user.password"
              :error="errors.password"
              type="password"
              :placeholder="t('password_ph')"
            />

            <BaseInput
              :label="t('password_confirm')"
              v-model="form.user.password_confirmation"
              :error="errors.password_confirmation"
              type="password"
              :placeholder="t('password_confirm_ph')"
            />

            <!-- Declarations -->
            <div class="md:col-span-2">
              <div
                class="p-4 border rounded-xl border-slate-200 dark:border-slate-700 bg-slate-50/70 dark:bg-slate-800/60"
              >
                <div class="flex items-center gap-2 mb-3">
                  <span
                    class="inline-flex items-center justify-center rounded-full size-7 bg-secondary/30 text-primary ring-1 ring-black/5"
                  >
                    <i class="mdi mdi-shield-check-outline text-[16px]"></i>
                  </span>

                  <h5
                    class="text-sm font-semibold text-slate-900 dark:text-white"
                  >
                    {{ t("declarations") }}
                  </h5>
                </div>

                <div class="space-y-4">
                  <!-- Accept Data -->
                  <div
                    class="p-3 border rounded-xl bg-slate-50/70 dark:bg-slate-800/60 border-slate-200 dark:border-slate-700"
                    :class="
                      errors.acceptData
                        ? 'border-red-300 dark:border-red-500/40'
                        : ''
                    "
                  >
                    <label
                      class="flex items-start gap-3 text-sm leading-relaxed cursor-pointer text-slate-700 dark:text-slate-300"
                    >
                      <input
                        type="checkbox"
                        v-model="form.acceptData"
                        class="mt-1 rounded border-slate-300 text-primary focus:ring-primary"
                      />
                      <span>{{ t("accept_data_text") }}</span>
                    </label>

                    <transition name="fade">
                      <div
                        v-if="errors.acceptData"
                        class="mt-2 flex items-start gap-2 text-xs text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-lg px-2.5 py-2"
                      >
                        <i
                          class="mdi mdi-alert-circle-outline text-[14px] mt-0.5"
                        ></i>
                        <span>{{ errors.acceptData }}</span>
                      </div>
                    </transition>
                  </div>

                  <!-- Accept Terms -->
                  <div
                    class="p-3 border rounded-xl bg-slate-50/70 dark:bg-slate-800/60 border-slate-200 dark:border-slate-700"
                    :class="
                      errors.acceptTerms
                        ? 'border-red-300 dark:border-red-500/40'
                        : ''
                    "
                  >
                    <label
                      class="flex items-start gap-3 text-sm leading-relaxed cursor-pointer text-slate-700 dark:text-slate-300"
                    >
                      <input
                        type="checkbox"
                        v-model="form.acceptTerms"
                        class="mt-1 rounded border-slate-300 text-primary focus:ring-primary"
                      />
                      <span>
                        {{ t("accept_terms_prefix") }}
                        <RouterLink
                          to="/terms-and-conditions"
                          class="text-primary underline underline-offset-2 hover:opacity-80"
                        >
                          {{ t("terms_and_policy") }}
                        </RouterLink>
                        {{ t("accept_terms_suffix") }}
                      </span>
                    </label>

                    <transition name="fade">
                      <div
                        v-if="errors.acceptTerms"
                        class="mt-2 flex items-start gap-2 text-xs text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-lg px-2.5 py-2"
                      >
                        <i
                          class="mdi mdi-alert-circle-outline text-[14px] mt-0.5"
                        ></i>
                        <span>{{ errors.acceptTerms }}</span>
                      </div>
                    </transition>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="flex items-center justify-end mt-6">
            <button
              class="h-10 px-6 text-sm font-semibold text-black rounded-md bg-secondary hover:bg-secondary/90 disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center gap-2"
              :disabled="isSubmitting"
              @click="submit"
            >
              <span
                v-if="isSubmitting"
                class="inline-block size-4 rounded-full border-2 border-black/40 border-t-black animate-spin"
                aria-hidden="true"
              ></span>
              <span>
                {{ isSubmitting ? t("saving") || "Saving..." : t("save") }}
              </span>
            </button>
          </div>
        </div>
      </div>

      <Toast
        :show="toast.show"
        :type="toast.type"
        :message="toast.message"
        @update:show="toast.show = $event"
      />
      <!-- </div> -->
    </section>

    <Footer />
  </div>
</template>

<script>
import { messages } from "@/i18n";
import api from "@/services/api";
import { clearAuthCookies } from "@/utils/authSession";
import { setVerifySessionFromApi } from "@/utils/pendingVerification";
import Navbar from "@/components/Navbar.vue";
import BaseInput from "@/components/BaseInput.vue";
import BaseSelect from "@/components/BaseSelect.vue";
import BaseTextarea from "@/components/BaseTextarea.vue";
import Footer from "@/components/Footer.vue";
import Toast from "@/components/Toast.vue";

export default {
  components: { Navbar, BaseInput, BaseSelect, BaseTextarea, Toast, Footer },

  data() {
    return {
      messages,
      lang: (
        document.documentElement.lang ||
        localStorage.getItem("app_lang") ||
        "ar"
      )
        .toLowerCase()
        .startsWith("en")
        ? "en"
        : "ar",

      _langObserver: null,

      form: {
        user: {
          name: "",
          email: "",
          phone: "",
          password: "",
          password_confirmation: "",
        },

        client: {
          name: "",
          activity: "",
          manager_name: "",
          client_type: "",
          activity_description: "",
        },

        acceptData: false,
        acceptTerms: false,
      },

      clientTypes: [
        { key: "client", label: "" },
        { key: "consultant", label: "" },
        { key: "office", label: "" },
      ],

      errors: {},
      selectedClientType: "startup",
      clientTypeOptions: [
        { value: "entrepreneur", label: "" },
        { value: "startup", label: "" },
        { value: "small_business", label: "" },
      ],
      consultantTypeOptions: [
        { value: "individual", label: "" },
        { value: "office", label: "" },
      ],
      toast: { show: false, type: "success", message: "" },
      _toastTimer: null,

      // ✅ NEW: submit loading state
      isSubmitting: false,
    };
  },

  mounted() {
    this.lang = (
      document.documentElement.lang ||
      localStorage.getItem("app_lang") ||
      "ar"
    )
      .toLowerCase()
      .startsWith("en")
      ? "en"
      : "ar";

    window.addEventListener("app:lang-changed", this.onLangChanged);

    this._langObserver = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type === "attributes" && m.attributeName === "lang") {
          const htmlLang = document.documentElement.lang || "ar";
          this.lang = htmlLang.toLowerCase().startsWith("en") ? "en" : "ar";
        }
      }
    });
    this._langObserver.observe(document.documentElement, { attributes: true });

    this.refreshI18nLabels();
  },

  watch: {
    lang() {
      this.refreshI18nLabels();
    },
  },

  computed: {
    percent() {
      return 100;
    },

    currentClientTypeLabel() {
      const m = this.clientTypes.find((t) => t.key === this.selectedClientType);
      return m ? m.label : "";
    },
  },

  methods: {
    t(key) {
      return (this.messages[this.lang] && this.messages[this.lang][key]) || key;
    },

    isSaudiPhone(v) {
      const s = String(v || "").trim();
      // Saudi mobile: 05XXXXXXXX
      return /^05\d{8}$/.test(s);
    },

    showToast(message, type = "success") {
      this.toast.message = message;
      this.toast.type = type;
      this.toast.show = true;

      if (this._toastTimer) clearTimeout(this._toastTimer);
      this._toastTimer = setTimeout(() => (this.toast.show = false), 2500);
    },

    setCookie(name, value, days = 0) {
      const isHttps = window.location.protocol === "https:";
      let cookieStr =
        `${name}=${encodeURIComponent(value)};` +
        `path=/;` +
        `samesite=lax` +
        (isHttps ? `; secure` : ``);

      if (days > 0) {
        const date = new Date();
        date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
        cookieStr += `; expires=${date.toUTCString()}`;
      }

      document.cookie = cookieStr;
    },

    validateAll() {
      this.errors = {};

      if (!this.form.client.name?.trim())
        this.errors.client_name = this.t("org_name_required");

      if (!this.form.client.activity?.trim())
        this.errors.activity = this.t("activity_required");

      if (!this.form.client.manager_name?.trim())
        this.errors.manager_name = this.t("manager_required");

      if (!this.form.client.client_type)
        this.errors.client_type = this.t("client_type_required");

      if (!this.form.client.activity_description?.trim())
        this.errors.activity_description = this.t(
          "simple_description_required",
        );

      if (!this.form.user.email?.trim())
        this.errors.email = this.t("email_required");

      if (!this.form.user.phone?.trim())
        this.errors.phone = this.t("phone_required");
      else if (!this.isSaudiPhone(this.form.user.phone))
        this.errors.phone = this.t("invalid_phone");

      if (!this.form.user.password)
        this.errors.password = this.t("password_required");

      if (!this.form.user.password_confirmation)
        this.errors.password_confirmation = this.t("password_confirm_required");

      if (
        this.form.user.password &&
        this.form.user.password_confirmation &&
        this.form.user.password !== this.form.user.password_confirmation
      ) {
        this.errors.password_confirmation = this.t("password_not_match");
      }

      if (!this.form.acceptData)
        this.errors.acceptData = this.t("accept_data_required");

      if (!this.form.acceptTerms)
        this.errors.acceptTerms = this.t("accept_terms_required");

      return Object.keys(this.errors).length === 0;
    },

    onLangChanged(e) {
      this.lang = e.detail;
    },

    refreshI18nLabels() {
      this.clientTypes = [
        { key: "client", label: this.t("type_client") },
        { key: "consultant", label: this.t("type_consultant") },
        { key: "office", label: this.t("type_office") },
      ];

      this.clientTypeOptions = [
        { value: "entrepreneur", label: this.t("opt_entrepreneur") },
        { value: "startup", label: this.t("opt_nascent") },
        { value: "small_business", label: this.t("opt_small_business") },
      ];

      this.consultantTypeOptions = [
        { value: "individual", label: this.t("opt_individual") },
        { value: "office", label: this.t("opt_consulting_office") },
      ];
    },

    toPath(key) {
      if (key === "client") return "/registerClient";
      if (key === "consultant") return "/regiserConsultant";
      if (key === "office") return "/registerOffice";
      return "#";
    },

    isActiveType(key) {
      const path = this.$route.path;
      if (key === "client") return path.startsWith("/registerClient");
      if (key === "consultant") return path.startsWith("/regiserConsultant");
      if (key === "office") return path.startsWith("/registerOffice");
      return false;
    },

    async submit() {
      if (this.isSubmitting) return;

      const ok = this.validateAll();
      if (!ok) {
        this.showToast(this.t("please_check_required"), "error");
        return;
      }

      this.isSubmitting = true;

      try {
        const payload = {
          user: this.form.user,
          client: this.form.client,
        };

        const res = await api.post("/clientUserRegister", payload);
        const data = res?.data || {};
        const token = data?.token || data?.data?.token || null;

        if (!token) {
          this.showToast(this.t("register_failed") || "Registration failed.", "error");
          return;
        }

        clearAuthCookies();
        setVerifySessionFromApi(data, "register");

        this.showToast(data.message || this.t("register_success"), "success");
        this.$router.replace({ name: "VerifyAccount" });
      } catch (e) {
        const data = e?.response?.data;
        const message =
          data?.message ||
          e?.message ||
          this.t("register_failed") ||
          "Registration failed.";

        this.errors = data?.errors
          ? this.mapApiErrors(data.errors)
          : { api: message };
        this.showToast(this.errors.api || message, "error");
      } finally {
        this.isSubmitting = false;
      }
    },

    mapApiErrors(apiErrors) {
      const mapped = {};
      const requiredKeyByField = {
        "user.name": "user_name_required",
        "user.email": "email_required",
        "user.phone": "phone_required",
        "user.password": "password_required",
        "user.password_confirmation": "password_confirm_required",
        "client.name": "org_name_required",
        "client.activity": "activity_required",
        "client.manager_name": "manager_required",
        "client.client_type": "client_type_required",
        "client.activity_description": "simple_description_required",
        "address.city": "city_required",
        "address.district": "district_required",
        "address.street": "street_required",
        "address.building_number": "building_no_required",
        "address.additional_number": "extra_no_required",
        "address.postal_code": "zip_required",
        "address.short_address": "short_address_required",
        "bank.account_holder_name": "bank_owner_required",
        "bank.account_number": "bank_account_required",
        "bank.bank_name": "bank_name_required",
        acceptData: "accept_data_required",
        acceptTerms: "accept_terms_required",
      };

      const resolveMessage = (key, msg) => {
        const normalized = String(msg || "")
          .trim()
          .toLowerCase();
        if (normalized === "invalid_phone") return this.t("invalid_phone");
        if (normalized === "invalid_email") return this.t("invalid_email");
        if (normalized === "password_min_8") return this.t("password_min_8");
        if (
          normalized === "required" ||
          normalized === "validation_required" ||
          normalized.includes("required")
        ) {
          const reqKey = requiredKeyByField[key];
          return reqKey ? this.t(reqKey) : this.t("required");
        }
        if (
          key === "user.password_confirmation" &&
          (normalized.includes("match") || normalized.includes("confirmation"))
        )
          return this.t("password_not_match");
        return msg;
      };

      Object.keys(apiErrors || {}).forEach((key) => {
        const msg = Array.isArray(apiErrors[key])
          ? apiErrors[key][0]
          : apiErrors[key];
        const localizedMsg = resolveMessage(key, msg);

        if (key.startsWith("user.")) {
          const field = key.replace("user.", "");
          if (field === "name") mapped["user_name"] = localizedMsg;
          else if (
            field === "password" &&
            String(msg).toLowerCase().includes("confirmation")
          )
            mapped["password_confirmation"] = localizedMsg;
          else mapped[field] = localizedMsg;
          return;
        }

        if (key.startsWith("client.")) {
          const field = key.replace("client.", "");
          if (field === "name") mapped["client_name"] = localizedMsg;
          else if (field === "activity_description")
            mapped["activity_description"] = localizedMsg;
          else if (field === "client_type")
            mapped["client_type"] = localizedMsg;
          else mapped[field] = localizedMsg;
          return;
        }

        mapped[key] = localizedMsg;
      });

      return mapped;
    },
  },

  beforeUnmount() {
    window.removeEventListener("app:lang-changed", this.onLangChanged);
    if (this._langObserver) this._langObserver.disconnect();
  },
};
</script>
